import React, { useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Modal, Spinner, Table } from 'react-bootstrap';
import { FaDownload, FaTrash, FaUpload } from 'react-icons/fa';
import ConfirmDialog from '../dialogs/ConfirmDialog';
import { usePlateRegistrationHistoryQuery } from '../../queries/plateRegistration.query';
import {
  useDeletePlateRegistrationMutation,
  useUploadPlateRegistrationMutation
} from '../../mutations/plateRegistration.mutation';
import { getPlateRegistrationDownloadUrl } from '../../requests/plateRegistration.request';
import { PlateRegistrationFile } from '../../types/plateRegistration.types';

const ACCEPTED = '.pdf,.jpg,.jpeg,.png,.heic,.tif,.tiff';
const MAX_BYTES = 15000000;

interface Props {
  show: boolean;
  onHide: () => void;
  plateId: string;
  plateNumber: string;
  clientId?: string;
}

const formatSize = (bytes: number | null | undefined) => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

function PlateRegistrationModal({ show, onHide, plateId, plateNumber, clientId }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [fileToDelete, setFileToDelete] = useState<PlateRegistrationFile | null>(null);

  const { data: history, isLoading } = usePlateRegistrationHistoryQuery(plateId, show);
  const uploadMutation = useUploadPlateRegistrationMutation(clientId);
  const deleteMutation = useDeletePlateRegistrationMutation(clientId, plateId);

  const current = useMemo(() => history?.find((file) => file.isCurrent), [history]);

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValidationError(null);
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_BYTES) {
      setValidationError('That file is larger than 15 MB. Please upload a smaller scan.');
      event.target.value = '';
      return;
    }

    uploadMutation.mutate({ plateId, file });
    event.target.value = '';
  };

  // The stored link is short lived, so fetch a fresh one at click time.
  const handleView = async (fileId: string) => {
    const url = await getPlateRegistrationDownloadUrl(fileId);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleConfirmDelete = () => {
    if (!fileToDelete) return;
    deleteMutation.mutate(fileToDelete.id, { onSettled: () => setFileToDelete(null) });
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Registration &mdash; {plateNumber}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {validationError && <Alert variant="danger">{validationError}</Alert>}

          {isLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              <div className="p-3 mb-3 bg-light rounded border">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="mb-1">Current registration</h6>
                    {current ? (
                      <div className="small text-muted">
                        {current.fileName} &middot; {formatSize(current.sizeInBytes)} &middot;
                        uploaded {formatDateTime(current.uploadedAt)}
                      </div>
                    ) : (
                      <div className="small text-danger fst-italic">
                        No registration on file for this plate
                      </div>
                    )}
                  </div>

                  <div className="d-flex gap-2">
                    {current && (
                      <>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleView(current.id)}
                          className="d-flex align-items-center gap-2">
                          <FaDownload /> View
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => setFileToDelete(current)}
                          className="d-flex align-items-center gap-2">
                          <FaTrash /> Delete
                        </Button>
                      </>
                    )}
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={uploadMutation.isLoading}
                      onClick={() => fileInputRef.current?.click()}
                      className="d-flex align-items-center gap-2">
                      {uploadMutation.isLoading ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <FaUpload />
                      )}
                      {current ? 'Replace' : 'Upload'}
                    </Button>
                  </div>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED}
                  onChange={handleFileSelected}
                  style={{ display: 'none' }}
                />
              </div>

              <h6 className="mb-2">History</h6>
              {history && history.length > 0 ? (
                <Table striped bordered size="sm">
                  <thead>
                    <tr>
                      <th>File</th>
                      <th>Size</th>
                      <th>Uploaded</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((file) => (
                      <tr key={file.id}>
                        <td>{file.fileName}</td>
                        <td>{formatSize(file.sizeInBytes)}</td>
                        <td>{formatDateTime(file.uploadedAt)}</td>
                        <td>
                          {file.deletedAt ? (
                            <Badge bg="danger">Deleted</Badge>
                          ) : file.isCurrent ? (
                            <Badge bg="success">Current</Badge>
                          ) : (
                            <Badge bg="secondary">Replaced</Badge>
                          )}
                        </td>
                        <td>
                          {!file.deletedAt && (
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0"
                              onClick={() => handleView(file.id)}>
                              View
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted fst-italic small">Nothing has been uploaded yet.</p>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmDialog
        isOpen={!!fileToDelete}
        label={<>Delete registration file</>}
        body={
          <>
            Are you sure you want to delete <strong>{fileToDelete?.fileName}</strong> for plate{' '}
            <strong>{plateNumber}</strong>? It will no longer count as a registration on file, but
            it stays in the history and can be recovered.
          </>
        }
        confirmText="Delete"
        isLoading={deleteMutation.isLoading}
        confirmAction={handleConfirmDelete}
        handleClose={() => setFileToDelete(null)}
      />
    </>
  );
}

export default PlateRegistrationModal;
